import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartApi } from '../services/api';

const CartContext = createContext();

// Generate or get session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('hob_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('hob_session_id', sessionId);
  }
  return sessionId;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(getSessionId);

  // Fetch cart from backend
  const fetchCart = useCallback(async () => {
    try {
      const response = await cartApi.get(sessionId);
      setCartItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching cart:', error);
      // Fallback to localStorage
      const savedCart = localStorage.getItem('hob_cart');
      if (savedCart) {
        setCartItems(JSON.parse(savedCart));
      }
    }
  }, [sessionId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Save to localStorage as backup
  useEffect(() => {
    localStorage.setItem('hob_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = async (product, quantity = 1) => {
    setLoading(true);
    try {
      const response = await cartApi.add(sessionId, product.id, quantity);
      setCartItems(response.data.items || []);
    } catch (error) {
      console.error('Error adding to cart:', error);
      // Fallback to local state
      setCartItems((prev) => {
        const existingItem = prev.find((item) => item.product_id === product.id);
        if (existingItem) {
          return prev.map((item) =>
            item.product_id === product.id
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        }
        return [...prev, {
          product_id: product.id,
          name: product.name,
          price: product.price,
          quantity,
          weight: product.weight,
          image: product.image,
        }];
      });
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    setLoading(true);
    try {
      const response = await cartApi.remove(sessionId, productId);
      setCartItems(response.data.items || []);
    } catch (error) {
      console.error('Error removing from cart:', error);
      setCartItems((prev) => prev.filter((item) => item.product_id !== productId));
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (quantity <= 0) {
      await removeFromCart(productId);
      return;
    }
    setLoading(true);
    try {
      const response = await cartApi.update(sessionId, productId, quantity);
      setCartItems(response.data.items || []);
    } catch (error) {
      console.error('Error updating cart:', error);
      setCartItems((prev) =>
        prev.map((item) =>
          item.product_id === productId ? { ...item, quantity } : item
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      await cartApi.clear(sessionId);
      setCartItems([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  const cartTotal = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  const cartCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartTotal,
        cartCount,
        isCartOpen,
        setIsCartOpen,
        loading,
        sessionId,
        fetchCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
